import React from 'react'
import Modal from 'react-bootstrap/Modal'
import { X } from 'react-bootstrap-icons'

/**
 * The one modal shell. Every modal in the panel renders through this so the
 * header, spacing, scroll behaviour and footer rule can never drift apart.
 *
 * Sizes map to purpose, not to how much happens to be in the modal:
 *   sm  confirm prompt          md  single focused task
 *   lg  record form or detail   xl  table, picker or workspace
 *
 * `icon` is reserved for the compact archetypes (task, confirm, picker,
 * workspace) — record forms and detail views give the title the full width.
 *
 * Because the dialog is always `scrollable`, the footer stays pinned while the
 * body scrolls. That is the whole reason submit is always reachable.
 *
 * Formik forms pass `formId` + `onSubmit`: the body is wrapped in a <form> with
 * that id and `<ModalActions formId>` puts a matching `form` attribute on the
 * submit button, so a button living in the footer still submits the form and
 * native `required` validation still fires.
 */
const AppModal = ({
  show,
  onHide,
  size = 'lg',
  title,
  subtitle,
  icon,
  tone = 'primary',
  hero,
  footer,
  children,
  formId,
  onSubmit,
  // Forms that validate through Formik pass this so native validation does not
  // fire first and block submit with its own bubble.
  noValidate = false,
  className = '',
  bodyClassName = '',
  headerAddon,
  ...rest
}) => {
  const body = formId ? (
    <form id={formId} onSubmit={onSubmit} noValidate={noValidate}>
      {children}
    </form>
  ) : (
    children
  )

  const closeButton = (
    <button
      type='button'
      className='app-modal__close'
      aria-label='Close'
      onClick={onHide}
    >
      <X />
    </button>
  )

  return (
    <Modal
      show={show}
      onHide={onHide}
      size={size}
      centered
      scrollable
      className={`app-modal ${hero ? 'app-modal--hero' : ''} ${className}`.trim()}
      {...rest}
    >
      {hero ? (
        <>
          {hero}
          {closeButton}
        </>
      ) : (
        <Modal.Header>
          {icon && (
            <span className={`app-modal__icon app-modal__icon--${tone}`}>
              {icon}
            </span>
          )}
          <div className='app-modal__heading'>
            <h2 className='app-modal__title'>{title}</h2>
            {subtitle && <p className='app-modal__subtitle'>{subtitle}</p>}
          </div>
          {headerAddon}
          {closeButton}
        </Modal.Header>
      )}

      <Modal.Body className={bodyClassName}>{body}</Modal.Body>

      {footer && <Modal.Footer>{footer}</Modal.Footer>}
    </Modal>
  )
}

/**
 * Hero header for detail modals — the record's image carries the title, with
 * the status badge overlaid instead of sitting in a read-only field.
 */
export const ModalHero = ({ src, alt, title, subtitle, badge, action }) => (
  <div className='app-modal__hero'>
    {src && <img className='app-modal__hero-img' src={src} alt={alt || title || ''} />}
    {badge && <div className='app-modal__hero-badge'>{badge}</div>}
    <div className='app-modal__hero-caption'>
      <div className='app-modal__heading'>
        <h2 className='app-modal__title'>{title}</h2>
        {subtitle && <p className='app-modal__subtitle'>{subtitle}</p>}
      </div>
      {action}
    </div>
  </div>
)

export default AppModal
